import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Box, Hidden, Typography } from '@mui/material';
import { selectError, selectIsPulseProject } from 'selector/surveySelector';
import { SurveyAlreadyCompletedException } from 'exception/SurveyAlreadyCompletedException';
import { SurveyClosedException } from 'exception/SurveyClosedException';
import { SurveyInDevelopmentException } from 'exception/SurveyInDevelopmentException';
import doNotDisturbOn from 'app/public/do_not_disturb_on.svg';
import surveyClosedWhite from 'app/public/survey-closed-white.svg';
import { Container } from 'component/Container/Container';
import { HeaderPure } from 'component/Header/HeaderPure';
import { Footer } from 'component/Footer/Footer';
import { LangNamespaceEnum } from 'shared/enums/LangNamespaceEnum';
import { PageContainer } from 'component/PageContainer/PageContainer';
import { CenteredContent } from 'component/CenteredContent/CenteredContent.s';
import { ThemeContext } from 'context/ThemeContext';
import { ThemeModeEnum } from 'shared/enums/ThemeModeEnum';
import { selectSurveyClosedMessage } from 'selector/metaSelector';
import { AccessDeniedException } from 'exception/AccessDeniedException';

import { surveyClosedIconSx } from './SurveyClosedPage.s';

export type SurveyClosedPageProps = {
  isNoSurveysCase?: boolean;
};

export const SurveyClosedPage: React.VFC<SurveyClosedPageProps> = ({ isNoSurveysCase = false }) => {
  const { t } = useTranslation(LangNamespaceEnum.SURVEY_UI_PAGES);
  const { themeMode } = useContext(ThemeContext);
  const isPulseProject = useSelector(selectIsPulseProject);
  const regularSurveyClosedMessage = useSelector(selectSurveyClosedMessage);

  const regularSurveyClosedMessageFormatted = regularSurveyClosedMessage
    .split('\n')
    .map((paragraph, index) => (
      <Typography key={index} sx={{ minHeight: 27 }}>
        {paragraph}
      </Typography>
    ));

  const error = useSelector(selectError) as any;

  let errorDescriptions: string[];
  let pageTitle = t('SurveyClosedPage.title');

  if (isNoSurveysCase || error?.code === AccessDeniedException.API_CODE) {
    errorDescriptions = [
      t('SurveyClosedPage.surveyCompletedDescription[1]'),
      t('SurveyClosedPage.surveyCompletedDescription[2]'),
    ];
    pageTitle = t('SurveyClosedPage.surveyCompletedTitle');
  } else {
    switch (error?.code) {
      case SurveyAlreadyCompletedException.API_CODE:
        errorDescriptions = [
          t('SurveyClosedPage.surveyCompletedDescription[1]'),
          t('SurveyClosedPage.surveyCompletedDescription[2]'),
        ];
        pageTitle = t('SurveyClosedPage.surveyCompletedTitle');
        break;
      case SurveyClosedException.API_CODE:
      case SurveyInDevelopmentException.API_CODE:
      default:
        errorDescriptions = [
          t('SurveyClosedPage.defaultDescription[1]'),
          t('SurveyClosedPage.defaultDescription[2]'),
        ];
    }
  }

  const surveyClosedIconSrc =
    themeMode === ThemeModeEnum.HIGH_CONTRAST_ACCESSIBLE ? surveyClosedWhite : doNotDisturbOn;

  return (
    <PageContainer
      headerElement={
        <>
          <Hidden lgDown>
            <HeaderPure shouldBePlacedAbsolutely />
          </Hidden>
          <Hidden lgUp>
            <Container>
              <HeaderPure shouldBePlacedAbsolutely />
            </Container>
          </Hidden>
        </>
      }
      footerElement={<Footer />}
    >
      {/* TODO: Create a new component that is shared across pages */}
      <CenteredContent data-testid="SurveyClosedPanel">
        <Box
          data-testid="SurveyClosedIcon"
          component="img"
          sx={surveyClosedIconSx}
          alt={t('SuccessPage.successImage.imgAlt')}
          src={surveyClosedIconSrc}
        />
        <Typography component="h1" variant="h3" mb="20px">
          {pageTitle}
        </Typography>
        {isPulseProject ||
        SurveyAlreadyCompletedException.API_CODE === error?.code ||
        isNoSurveysCase
          ? errorDescriptions.map((description, index) => (
              <Typography key={index} textAlign="center" variant="body1">
                {description}
              </Typography>
            ))
          : regularSurveyClosedMessageFormatted}
      </CenteredContent>
    </PageContainer>
  );
};
